/* AI product-finder helper — collection pages (pilot 2026-08-18).
 *
 * Streams an answer from the sync worker's /chat endpoint and renders the products the
 * assistant actually retrieved as cards. The worker holds the Meilisearch chat key; this
 * file never sees a credential.
 *
 * Two details worth knowing before editing:
 *
 * 1. Meilisearch only implements STREAMED chat completions, so the response is SSE and has
 *    to be reassembled. Tool-call arguments arrive as fragments split across events and
 *    must be accumulated BY INDEX — concatenating them blindly, or keeping only the last
 *    fragment, yields unparseable JSON and silently no product cards.
 *
 * 2. Adding to cart needs a VARIANT id, not a product id. The index now carries a compact
 *    `variants` array, so single-variant products can be added straight from the answer.
 *    Anything with real options is deliberately sent to the product page instead — silently
 *    choosing a size for someone is precisely the wrong move in this category.
 */
(function () {
  var root = document.getElementById('llm-cah');
  if (!root) return;
  /* Run ONCE per panel however many times this file is loaded. The homepage includes it from
     the hero section while the theme also loads it, and the second run appended a second
     "Start over" button and a second set of listeners — every question would have been sent
     twice (owner 2026-08-20). */
  if (root.dataset.llmCahInit) return;
  root.dataset.llmCahInit = '1';

  var ENDPOINT = root.dataset.endpoint;
  var CONTEXT = root.dataset.context || '';
  /* The collection HANDLE, which scopes retrieval server-side. CONTEXT (the title) reads
     nicely in the prompt but means nothing to the index, and sending only the title left
     every question searching the entire store. */
  var COLLECTION = root.dataset.collection || '';
  var convo = document.getElementById('llm-cah-convo');
  var inner = convo.firstElementChild;
  var input = document.getElementById('llm-cah-q');
  var form = root.querySelector('.llm-cah__ask');
  var go = root.querySelector('.llm-cah__go');
  var convoHistory = [];
  /* Every product retrieved across the whole conversation — see the note where it is filled.
     Cleared by "Start over" alongside convoHistory. */
  var seenDocs = [];
  var busy = false;
  var ORIGINAL_PLACEHOLDER = input.placeholder;

  /* "Start over" — added because a conversation had no exit: once it was running, the
     chips were gone, the box said "Ask a follow-up question", and every new question was
     appended to a thread that might be about something else entirely. Someone shopping for
     their mother after asking about their father was stuck carrying the wrong context.
     Built in JS rather than Liquid so it only exists once a conversation does. */
  var resetBtn = document.createElement('button');
  resetBtn.type = 'button';
  resetBtn.className = 'llm-cah__reset';
  resetBtn.textContent = 'Start over';
  resetBtn.hidden = true;
  root.querySelector('.llm-cah__bar').appendChild(resetBtn);
  /* Bound to the BUTTON, not delegated from the panel. The homepage modal moves this button
     into its own header so it stays put as the conversation grows — and a delegated handler
     on #llm-cah stops firing the moment the button leaves that element, which silently broke
     "Start over" there (owner 2026-08-20). A direct listener survives re-parenting. */
  resetBtn.addEventListener('click', resetConversation);

  function resetConversation() {
    turns.length = 0;
    convoHistory.length = 0;
    seenDocs.length = 0;
    inner.innerHTML = '';
    convo.classList.remove('is-open');
    root.classList.remove('llm-cah--active');   // brings the suggested chips back
    input.placeholder = ORIGINAL_PLACEHOLDER;
    input.value = '';
    resetBtn.hidden = true;
    busy = false;
    go.disabled = false;
    /* Same reason the homepage modal does not autofocus on open (llm-hero-finder.js): on a phone
       this raises the on-screen keyboard, which covers the suggestion chips that "Start over" just
       brought back — so the reset hides the very thing it restored. Desktop keeps the focus, where
       landing in the input after a reset is exactly what you want. */
    if (!(window.matchMedia && window.matchMedia('(pointer: coarse)').matches)) input.focus();
  }

  /* No placement logic here any more: the panel is rendered server-side inside
     main-collection-products.liquid, in the results column above the count. It used to be
     its own section that this file MOVED into position after load, which meant a visible
     layout shift on every page view. */

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* Answer formatting is SHARED — assets/llm-answer-format.js, loaded ahead of this file and used
     by llm-meilisearch-search.js (/search) too. This used to be its own near-copy, and the two had
     already drifted: /search turned "1." "2." into separate list blocks and this one did not, so
     the same answer read as a tidy list on one page and a wall of text on the other.

     Falls back to escaped plain text if the shared script is missing. Unformatted is ugly;
     unescaped would be a vulnerability, and model output is never trusted. */
  function fmt(t) {
    return window.LLM_ANSWER ? window.LLM_ANSWER.format(t) : esc(t);
  }

  function money(n) {
    return '$' + Number(n || 0).toFixed(2);
  }

  function cardHtml(entry) {
    var d = entry.d || entry;
    var n = entry.n || null;
    var vs = Array.isArray(d.variants) ? d.variants : [];
    var buyable = vs.filter(function (v) { return v.available; });
    var single = vs.length === 1;
    var url = '/products/' + d.handle;

    var action;
    if (!d.available || !buyable.length) {
      action = '<a class="llm-cah__btn llm-cah__btn--ghost" href="' + esc(url) + '">View details</a>';
    } else if (single) {
      action = '<button class="llm-cah__btn" data-add="' + esc(vs[0].id) + '">Add to cart</button>';
    } else {
      /* Multi-variant products open the theme's own quick-buy slideout rather than sending
         the customer away to the product page — same behaviour, and same "Add to cart"
         label, as the product cards in the grid below (owner 2026-08-18).
         quick-add.js listens on the DOCUMENT for `.js-quick-add` clicks, so a card injected
         after page load is picked up with no re-binding. It matches on evt.target itself,
         which is why the label sits directly in the button with no wrapper element. */
      action = '<button type="button" class="llm-cah__btn js-quick-add" aria-haspopup="dialog"' +
        ' data-product-url="' + esc(url) + '" data-product-default-variant="false">Add to cart</button>';
    }

    return '<article class="llm-cah__card">' +
      (n ? '<span class="llm-cah__num" aria-label="Recommendation ' + n + '">' + n + '</span>' : '') +
      (d.image ? '<img class="llm-cah__thumb" src="' + esc(d.image) + '" alt="' + esc(d.title) + '" loading="lazy" width="200" height="96">' : '<div class="llm-cah__thumb"></div>') +
      '<p class="llm-cah__name"><a href="' + esc(url) + '">' + esc(d.title) + '</a></p>' +
      /* "From $28.99", not "$28.99 from" — the qualifier belongs before the number, the
         way the product cards elsewhere on the site read it. */
      '<p class="llm-cah__price">' + (vs.length > 1 ? '<span>From </span>' : '') + money(d.price_min) + '</p>' +
      '<div class="llm-cah__tags">' +
        (d.metafield__custom_fsa_hsa_eligible ? '<span class="llm-cah__tag">FSA/HSA</span>' : '') +
        (!d.available ? '<span class="llm-cah__tag llm-cah__tag--warn">Out of stock</span>' : '') +
        (vs.length > 1 ? '<span class="llm-cah__tag llm-cah__tag--plain">' + vs.length + ' options</span>' : '') +
      '</div>' + action +
    '</article>';
  }

  /* Only show cards for products the assistant ACTUALLY named, in the order it named them.
     Previously every retrieved document became a card, so a three-product answer produced
     six cards with two unmentioned products wedged in between - the text said "1, 2, 3"
     while the cards read 1, 2, ?, ?, 3, ? which reads like the recommendation is wrong.
     Match on the first few significant words of the title, since the model reformats names
     ("BetterDry Overnight Adult Diapers with Plastic Backing" vs "... w/ Plastic Backing").
     A purely conversational reply — an answer to a yes/no question, or an offer to show
     options if the customer wants them — gets NO cards. Showing retrieval results there
     answers a question the customer hasn't asked yet. */
  function orderByMention(answer, docs) {
    if (!answer || !docs.length) return [];
    var hay = answer.toLowerCase().replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ');
    /* Match on the LONGEST title prefix that appears in the reply, minimum four words.
       Three words was far too loose for this catalogue, where product families share
       prefixes: "Abena Pants Premium" matches Level 1 AND Level 2, and a two-word fallback
       of "Abena Pants" also caught Level 0. One mention claimed several products, so a
       five-product answer rendered eight numbered cards including items the assistant
       never recommended — which is also how guardrail-excluded products can slip back in. */
    /* Token OVERLAP, not a contiguous prefix. The model paraphrases product names — the
       catalogue says "BetterDry Pull-On Adult Diapers", it writes "BetterDry Overnight
       Pull-On Adult Diapers" — and one inserted word broke a prefix match, silently
       dropping that product's card. Overlap survives inserted, reordered and dropped words.
       Scored on the title's first six meaningful words (brand + distinguishing terms);
       "adult"/"diapers" are too common to identify anything, so they don't count toward
       the match. `at` is the position of the earliest matched word, which keeps the cards
       in the order the reply names them. */
    var GENERIC = { adult: 1, diapers: 1, diaper: 1, underwear: 1, the: 1, for: 1, with: 1, and: 1, pull: 1, on: 1 };

    /* The words must appear TOGETHER, as a name — not scattered anywhere in the reply.
       Counting loose word hits meant "BetterDry Day Adult Diapers" claimed a card off
       "BetterDry" in one sentence, "day" inside "during the day" in another, and "plastic"
       in a third; "Prevail Nu-Fit" claimed one off "Prevail" plus the "fit" inside
       "Dri-Fit™". Two of three cards were products the assistant never recommended (owner
       2026-08-19) — the same class of failure that once resurfaced a guardrailed product.
       So the distinguishing words must occur in order, within one sentence, separated by at
       most a few words. That still tolerates the paraphrase this needs to survive
       ("BetterDry Overnight Adult Diapers" for "BetterDry Adult Diapers"), because gaps are
       allowed — it just won't span a sentence boundary or half a paragraph. */
    function reEsc(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }
    function nearRe(list) { return new RegExp(list.map(reEsc).join('[^.!?]{0,30}?'), 'i'); }

    var scored = docs.map(function (d) {
      /* Keep DIGITS, and look past the first six words. "Abena Pants Premium Pull-On
         Underwear Level 3" and "... Level 2" both reduced to `abena pants premium` — the
         only thing telling them apart is a numeral seven words in, which the old window
         never reached and the length filter would have dropped anyway. Both then matched at
         the same position and the dedupe below kept just one, so an answer naming both
         showed a card for only one of them (owner 2026-08-19).
         Level/size numerals are exactly what distinguishes products in this catalogue, so
         they cannot be treated as noise. Eight words is enough to reach them without
         dragging in pack and backing descriptions. */
      var words = String(d.title || '').toLowerCase().replace(/[^a-z0-9 ]+/g, ' ').split(/\s+/)
        .filter(Boolean);
      var keyWords = words.filter(function (w) {
        return (w.length > 2 || /^\d+$/.test(w)) && !GENERIC[w];
      }).slice(0, 8);
      if (keyWords.length < 2) keyWords = words.slice(0, 2);
      if (keyWords.length < 2) return { d: d, at: -1, len: 0 };

      /* Try the full run of distinguishing words, then a shorter leading run — the model may
         drop a trailing word, but dropping the LEADING ones would stop identifying the
         product at all, so at least the first two must match together. */
      var hit = null, used = 0;
      for (var take = keyWords.length; take >= 2; take--) {
        hit = hay.match(nearRe(keyWords.slice(0, take)));
        if (hit) { used = take; break; }
      }
      return { d: d, at: hit ? hit.index : -1, len: used };
    });

    /* Two products can still match at the SAME position (Level 1 matched on four words,
       Level 2 on four words, both starting where the reply names one of them). Keep the
       longer — more specific — match and drop the other, so each thing the reply names
       yields exactly one card. */
    var byPos = {};
    scored.filter(function (s) { return s.at > -1; }).forEach(function (s) {
      var cur = byPos[s.at];
      if (!cur || s.len > cur.len) byPos[s.at] = s;
    });
    var mentioned = Object.keys(byPos)
      .map(function (k) { return byPos[k]; })
      .sort(function (a, b) { return a.at - b.at; });
    /* Cards show ONLY what the assistant named — nothing retrieved-but-unmentioned, and
       nothing at all when it named nothing.
       Both fallbacks here were wrong in the same way. Asked in Incontinence Swimwear whether
       those products suit urinary incontinence, the assistant correctly answered no and
       OFFERED to show alternatives if wanted — and three cards appeared under it anyway,
       including Tranquility Swimmates, the very product it had just ruled out (owner
       2026-08-19). Retrieval is a search result; a recommendation is a decision. Only the
       decision belongs on screen, or the cards contradict the words above them. */
    if (!mentioned.length) return [];

    /* Numbered to match the order they appear in the reply, so "the second one" in the text
       and the card badged 2 are unmistakably the same product. */
    return mentioned.map(function (s, i) { return { d: s.d, n: i + 1 }; });
  }

  /* What do the recommended products have in COMMON? Those shared attributes are what the
     customer actually asked for ("overnight", "tab-style"), so they make a meaningful
     filter for the grid below — better than filtering to just the three recommended
     handles, since the shopper can then see every product that fits, not only our picks.
     A field only counts if EVERY recommended product shares a value: two overnight briefs
     and one moderate pull-on have nothing honest in common to filter by. */
  var FACET_FIELDS = [
    { key: 'metafield__custom_absorbency_level', noun: function (v) { return v.toLowerCase() + ' absorbency'; } },
    { key: 'metafield__custom_producttype', noun: function (v) { return v.toLowerCase(); } },
    { key: 'metafield__custom_gender', noun: function (v) { return 'for ' + v.toLowerCase(); } },
  ];

  function sharedFacets(docs) {
    var params = [], words = [];
    if (docs.length < 2) return { params: params, label: '' };
    FACET_FIELDS.forEach(function (f) {
      var sets = docs.map(function (d) {
        var v = d[f.key];
        return v == null ? [] : [].concat(v).map(String);
      });
      if (sets.some(function (s) { return !s.length; })) return;
      var common = sets[0].filter(function (v) {
        return sets.every(function (s) { return s.indexOf(v) > -1; });
      });
      if (!common.length) return;
      params.push('f.' + f.key + '=' + encodeURIComponent(common[0]));
      words.push(f.noun(common[0]));
    });
    return { params: params, label: words.join(', ') };
  }

  /* `i` is the turn's index, supplied by turns.map(). It has to ride on the markup because
     render() rebuilds inner.innerHTML from scratch on every streaming chunk — any expand/collapse
     state held in the DOM would be wiped several times a second. The flag lives on the turn
     object and the index is how a click finds its way back to it. */
  function turnHtml(t, i) {
    var html = '<div class="llm-cah__turn">';
    html += '<p class="llm-cah__said">' + esc(t.question) + '</p>';
    if (t.status) html += '<p class="llm-cah__status">' + esc(t.status) + '</p>';
    /* While a retry runs, the FIRST attempt stays on screen (dimmed) until the replacement
       has real content. Blanking it made the answer visibly vanish and restart, which reads
       as a glitch — the customer has no idea a retry happened, only that their answer
       disappeared. The dimmed copy is swapped out the moment new text arrives. */
    /* NO CLAMP HERE. Answers in THIS panel are never collapsed: every one of them was asked for
       deliberately, either by typing into this box or by tapping a suggestion chip. Hiding an
       answer someone explicitly requested would be perverse (owner, 2026-09-13).

       The collapse lives only in llm-meilisearch-search.js, which shows an answer nobody asked
       for — auto-generated from whatever went into the top search bar — where it competes with
       the product results the customer actually came for. */
    if (t.answer) html += '<p class="llm-cah__reply" data-turn="' + i + '">' + fmt(t.answer) + '</p>';
    else if (t.carry) html += '<p class="llm-cah__reply llm-cah__reply--stale" data-turn="' + i + '">' + fmt(t.carry) + '</p>';
    /* ONLY products the reply actually names are shown, numbered to match it.
       An "Others that may suit" section used to display the rest of the search results —
       and that silently bypassed the merchandising guardrails. Crinklz printed diapers are
       excluded from recommendations unless a customer asks for printed/ABDL products; the
       assistant correctly never named them, but the extras list put them on screen anyway.
       Anything a guardrail can keep out of the ANSWER must also be kept out of the CARDS,
       so the cards now mirror the recommendation exactly. */
    var entries = orderByMention(t.answer, t.docs || []);
    var show = entries.filter(function (e) { return e.n; });
    if (show.length) {
      html += '<div class="llm-cah__cards">' + show.map(cardHtml).join('') + '</div>';
      /* Only when there IS a collection grid beneath to filter. In global mode (the homepage
         modal) the button would promise to narrow products that aren't on the page. */
      var f = COLLECTION ? sharedFacets(show.map(function (e) { return e.d; })) : { params: [] };
      if (f.params.length) {
        html += '<button type="button" class="llm-cah__apply" data-filters="' +
          esc(f.params.join('|')) + '">Show all ' + esc(f.label) + ' in this collection</button>';
      }
    }
    return html + '</div>';
  }

  /* Turns ACCUMULATE. Replacing the panel on each question threw away the previous answer,
     so a customer comparing two suggestions lost the first one the moment they asked about
     the second. */
  var turns = [];

  function render() {
    /* Follow the newest content INSIDE the conversation box, never by scrolling the page.
       The box is height-capped, so a second or third answer streamed in below the fold of
       that box and simply wasn't visible — the customer had to scroll the panel by hand to
       find the reply they had just asked for (owner 2026-08-19).
       Sticky-bottom, not forced: if they have scrolled up to re-read something, the view
       stays where they put it. Page scroll is untouched, which is what made the earlier
       scrollIntoView unpleasant — it jumped to the last product card mid-read. */
    var stick = inner.scrollHeight - inner.scrollTop - inner.clientHeight < 60;

    inner.innerHTML = turns.map(turnHtml).join('') +
      '<p class="llm-cah__disclaim">Product guidance only &mdash; not medical advice. ' +
      'For health questions please speak with your doctor or nurse.</p>';

    if (stick) inner.scrollTop = inner.scrollHeight;
    convo.classList.add('is-open');
    root.classList.add('llm-cah--active');
    resetBtn.hidden = false;
    // After the first answer the box is a follow-up box, and should say so.
    input.placeholder = 'Ask a follow-up question…';
  }

  /* isRetry is a PARAMETER, not a flag on the turn. The guard used to live on the turn
     object — but the retry pops that turn and pushes a fresh one, so the flag vanished and
     each attempt looked like a first attempt. It retried repeatedly, visibly rewriting the
     answer several times and spending an API call each round. */
  async function ask(question, isRetry, carryOver) {
    if (busy || !question) return;
    busy = true;
    go.disabled = true;

    var turn = { question: question, answer: '', docs: [], status: 'Thinking', carry: carryOver || '' };
    turns.push(turn);
    render();
    /* Bring the new question into view WITHIN the capped conversation box. This scrolls the
       box only, never the page — page-level scrolling is what caused the jump-to-last-card
       complaint, and this deliberately avoids it. */
    inner.scrollTop = inner.scrollHeight;

    convoHistory.push({ role: 'user', content: question });

    var res;
    try {
      res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: convoHistory.slice(-12),
          context: CONTEXT,
          collection: COLLECTION,
        }),
      });
    } catch (e) {
      turn.status = '';
      turn.answer = 'Sorry — we couldn’t reach the assistant. Please try again.';
      render(); busy = false; go.disabled = false;
      return;
    }

    if (!res.ok) {
      /* Show the server's own reason when it sends one. Search failures and model failures
         both surfaced as one generic "assistant is unavailable", which told us nothing —
         a Meilisearch 400 from a non-filterable attribute looked identical to an outage
         and took a live customer report to find (owner 2026-08-18). The worker's messages
         are written for shoppers, so they're safe to show as-is. */
      var reason = null;
      try { reason = (await res.json()).error || null; } catch (e) { /* not JSON */ }
      turn.status = '';
      turn.answer = res.status === 429
        ? 'That’s a lot of questions in a short time — please wait a moment and try again.'
        : (reason || 'Sorry — the assistant is unavailable right now.');
      if (window.console) console.warn('[llm-cah] chat failed', res.status, reason);
      render(); busy = false; go.disabled = false;
      return;
    }

    var reader = res.body.getReader();
    var dec = new TextDecoder();
    var buf = '', answer = '', docs = [];
    var calls = {}; // tool-call fragments, keyed by index (see header note)

    while (true) {
      var chunk = await reader.read();
      if (chunk.done) break;
      buf += dec.decode(chunk.value, { stream: true });
      var lines = buf.split('\n');
      buf = lines.pop(); // keep the trailing partial line for the next chunk

      for (var i = 0; i < lines.length; i++) {
        var line = lines[i];
        if (line.indexOf('data: ') !== 0 || line.indexOf('[DONE]') > -1) continue;
        var ev;
        try { ev = JSON.parse(line.slice(6)); } catch (e) { continue; }
        var delta = (ev.choices && ev.choices[0] && ev.choices[0].delta) || {};
        if (delta.content) {
          answer += delta.content;
          turn.answer = answer;
          turn.carry = '';        // real content has arrived; drop the dimmed first attempt
          turn.status = '';
          render();
        }
        var tcs = delta.tool_calls || [];
        for (var j = 0; j < tcs.length; j++) {
          var tc = tcs[j];
          var k = tc.index == null ? 0 : tc.index;
          if (!calls[k]) calls[k] = { name: '', args: '' };
          if (tc.function && tc.function.name) calls[k].name = tc.function.name;
          if (tc.function && tc.function.arguments) calls[k].args += tc.function.arguments;
        }
      }
    }

    /* Extracting the retrieved products is fiddlier than the docs suggest. In practice
       Meilisearch streams SEVERAL JSON objects concatenated into a single tool-call slot
       (the search request, the assistant echo, the results, the tool reply) — so the
       accumulated `args` is not one parseable object — and the products sit under a
       "sources" key, not the "documents" key the tool schema advertises.
       So: scan the raw string for "sources": and bracket-match the array that follows.
       Bracket matching is string-aware, otherwise a "]" inside a product title (common
       here: sizes like 10" x 12") would truncate the array early. */
    function extractSources(raw) {
      var out = [];
      var at = 0;
      while (true) {
        var i = raw.indexOf('"sources"', at);
        if (i === -1) break;
        var start = raw.indexOf('[', i);
        if (start === -1) break;
        var depth = 0, inStr = false, escNext = false, end = -1;
        for (var p = start; p < raw.length; p++) {
          var ch = raw[p];
          if (escNext) { escNext = false; continue; }
          if (ch === '\\') { escNext = true; continue; }
          if (ch === '"') { inStr = !inStr; continue; }
          if (inStr) continue;
          if (ch === '[') depth++;
          else if (ch === ']') { depth--; if (depth === 0) { end = p; break; } }
        }
        if (end === -1) break;
        try { out = out.concat(JSON.parse(raw.slice(start, end + 1))); } catch (e) { /* skip */ }
        at = end + 1;
      }
      return out;
    }

    Object.keys(calls).forEach(function (k) {
      extractSources(calls[k].args).forEach(function (d) {
        if (d && d.handle && !docs.some(function (x) { return x.handle === d.handle; })) docs.push(d);
      });
    });

    convoHistory.push({ role: 'assistant', content: answer });
    /* Carry every product seen so far in this conversation, not just this turn's search
       results. Asked a follow-up ("Daytime"), the assistant named two products but only one
       got a card: the other came from the PREVIOUS turn's list, which it can still see in the
       conversation history while this turn's retrieval no longer returned it (owner
       2026-08-19). The reply is what decides which cards show; the pool it draws from has to
       be everything the assistant could legitimately be referring to. */
    docs.forEach(function (d) {
      if (!seenDocs.some(function (x) { return x.handle === d.handle; })) seenDocs.push(d);
    });
    turn.docs = seenDocs.slice();
    turn.status = '';

    /* SAFETY NET for a real, intermittent failure: the model sometimes replies "I'm
       searching… please allow me a moment" and simply stops — no search runs, no products
       arrive, and the customer is left staring at a half-finished sentence forever.
       Detected by: no products retrieved AND the reply reads like an announcement. Retried
       once, silently, because a second attempt almost always completes. Only once — a loop
       here would burn tokens on every failure. */
    /* Broadened from a phrase list to the actual danger signal: NO products were retrieved
       yet the reply reads like a recommendation. Chasing individual phrases was hopeless —
       "I'll show you some options" slipped past a list built for "let me search", and that
       reply went on to recommend "Depend FIT-FLEX", which this shop does not sell. When no
       search ran, every product name in the reply is unverifiable. A closing question with
       no products is fine (it's asking, not recommending); prose of any length without
       products is not. */
    var looksLikeRecommendation = answer.length > 160 && !/\?\s*$/.test(answer.trim());
    if (!docs.length && looksLikeRecommendation && !isRetry) {
XKEEPX
      render();
      convoHistory.pop();          // drop the dead reply so it can't be treated as context
      busy = false;
      go.disabled = false;
      turns.pop();                       // the retry pushes a fresh turn for the same question
      return ask(question, true, answer); // isRetry: cannot retry again; answer stays on screen
    }

    /* Retry already used and STILL nothing retrieved: the reply names products that cannot
       be checked against the catalogue, and we know from "Depend FIT-FLEX" that these can
       be products the shop does not carry. Showing an invented recommendation is worse than
       showing none, so it is replaced rather than displayed with a caveat — a caveat under
       a confident product description does not stop anyone buying the wrong thing. */
    if (!docs.length && isRetry && looksLikeRecommendation) {
      turn.answer = 'Sorry — I couldn’t search the catalogue just then, so I can’t recommend ' +
        'anything reliably. Please try asking again, or contact our care team and they’ll ' +
        'help you find the right product.';
    }

    render();
    busy = false;
    go.disabled = false;
    /* Deliberately NO scrollIntoView here. Scrolling to the newest content yanked the page
       down to the last product card the moment the cards rendered — which on mobile hits
       just as the customer has started reading the answer above. The answer streams in
       from the top; let them read at their own pace. */
  }

  form.addEventListener('submit', function (e) {
    e.preventDefault();
    var q = input.value.trim();
    if (q) { ask(q); input.value = ''; }
  });

  root.addEventListener('click', function (e) {
    // Reset is bound directly on the button (see where it is created) so it keeps working
    // when the modal moves it into its own header. Handling it here too would fire twice.

    var chip = e.target.closest('.llm-cah__chip');
    if (chip) { ask(chip.textContent.trim()); return; }

    /* Applies the shared attributes to the collection grid below. The grid keeps its filter
       state in the URL (?f.<field>=<value>) and rebuilds on `popstate`, so pushing the URL
       and firing that event re-renders it in place — no reload, so the conversation above
       survives. Existing f.* params are cleared first so two questions in a row don't
       stack contradictory filters. */
    var apply = e.target.closest('.llm-cah__apply');
    if (apply) {
      var p = new URLSearchParams(location.search);
      [...p.keys()].forEach(function (k) { if (k.indexOf('f.') === 0 || k === 'page') p.delete(k); });
      apply.dataset.filters.split('|').forEach(function (pair) {
        var i = pair.indexOf('=');
        if (i > 0) p.set(pair.slice(0, i), decodeURIComponent(pair.slice(i + 1)));
      });
      var qs = p.toString();
      history.pushState({ ms: true }, '', location.pathname + (qs ? '?' + qs : ''));
      window.dispatchEvent(new PopStateEvent('popstate'));
      apply.textContent = 'Filters applied to the products below ✓';
      apply.disabled = true;
      var grid = document.getElementById('llm-meilisearch-count');
      if (grid) grid.scrollIntoView({ behavior: 'instant', block: 'center' });
      return;
    }

    var add = e.target.closest('[data-add]');
    if (add) {
      add.disabled = true;
      var label = add.textContent;
      add.textContent = 'Adding…';
      /* Ask the Cart API to re-render the icon bubble in the same round trip, exactly as
         product-form.js does. The previous version fired a `cart:refresh` event, which this
         theme doesn't listen for — so the item really was added, but the header count and
         the drawer never changed and it looked like the button did nothing (owner
         2026-08-18). Refreshing the drawer and opening it gives the same confirmation the
         product cards below already give. */
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          /* `_source` is underscore-prefixed so the customer never sees it, but it rides the line
             all the way onto the order — which is what makes "did the product finder actually sell
             anything?" answerable from order data rather than from click events. */
          items: [{ id: Number(add.dataset.add), quantity: 1,
                    properties: { _source: 'collection-helper' } }],
          sections: 'cart-icon-bubble',
          sections_url: window.location.pathname,
        }),
      })
        .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
        .then(function (data) {
          add.textContent = 'Added ✓';

          var bubble = document.getElementById('cart-icon-bubble');
          if (bubble && data.sections && data.sections['cart-icon-bubble']) {
            bubble.innerHTML = data.sections['cart-icon-bubble'];
          }

          var drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.refresh === 'function') {
            drawer.refresh(true);
            if (typeof drawer.open === 'function') drawer.open(add);
          }
        })
        .catch(function () { add.textContent = label; add.disabled = false; });
    }
  });

  /* ---------- TAG THE QUICK-ADD PATH TOO ----------

     The `[data-add]` handler above only exists for SINGLE-VARIANT products. Anything with a size or
     pack option renders `.js-quick-add` instead and hands off to the theme's quick-buy drawer, which
     posts `new FormData(this.form)` from product-form.js and knows nothing about `_source`.

     That is not an edge case here. Measured 2026-09-10: the finder runs on the incontinence
     collections, where 94-97% of products are multi-variant (adult-diapers 96.0%, briefs-tab-style
     97.0%, pull-up-style 94.9%, bariatric-diapers 97.1%) — exactly the collections it is used on.
     So ~96% of what the finder recommended went untagged, and zz_feature_adoption.mjs reported it as
     having sold NOTHING in 60 days. It had no way to know either way.

     ⚠️ THE SUBMIT LISTENER MUST BE CAPTURE-PHASE. product-form.js handles `submit` on the form and
     immediately reads new FormData(this.form); a bubble-phase listener would run AFTER that read and
     append a field nobody looks at. Capture descends from document first, so the input is in place
     before the form is serialised. */
  var quickAddFromFinder = false;

  /* Recomputed on EVERY quick-add click, not just the finder's, so a later click on an ordinary
     product card cannot inherit a stale "yes" and be mislabelled as a finder sale. */
  document.addEventListener('click', function (e) {
    var qa = e.target && e.target.closest && e.target.closest('.js-quick-add');
    if (!qa) return;
    quickAddFromFinder = !!qa.closest('.llm-cah__card');
  }, true);

  document.addEventListener('submit', function (e) {
    if (!quickAddFromFinder) return;
    var form = e.target;
    if (!form || !form.closest || !form.closest('quick-add-drawer')) return;
    /* The drawer reuses its markup for the same product, so this can run twice on one form. */
    if (form.querySelector('input[name="properties[_source]"]')) return;
    var input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'properties[_source]';
    input.value = 'collection-helper';
    form.appendChild(input);
  }, true);
})();
